import React, { useState, useCallback } from 'react';

function InfoBubble({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: '4px', cursor: 'pointer' }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#e5e7eb',
        fontSize: '11px', fontWeight: 700, color: '#6b7280', lineHeight: 1,
      }}>?</span>
      {show && (
        <span style={{
          position: 'absolute', bottom: '22px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: '#1f2937', color: '#fff', fontSize: '11px', padding: '6px 10px',
          borderRadius: '6px', whiteSpace: 'normal', width: '220px', zIndex: 10,
          lineHeight: '1.4', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>{text}</span>
      )}
    </span>
  );
}

const RESPONSE_TYPES = [
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'scale', label: 'Scale' },
];

const EMPTY_OPTION = {
  response_value: '',
  button_label: '',
  engagement_points: 0,
  display_heading: '',
  display_body: '',
  display_cta_text: '',
  display_cta_url: '',
};

const EMPTY_SCALE_BUCKET = {
  range_min: '',
  range_max: '',
  engagement_points: 0,
  display_heading: '',
  display_body: '',
  display_cta_text: '',
  display_cta_url: '',
};

const SCALE_PRESETS = [
  { label: '1–3', min: 1, max: 3 },
  { label: '1–5', min: 1, max: 5 },
  { label: '1–10', min: 1, max: 10 },
];

function makeDefaultScaleBuckets(scaleMin, scaleMax) {
  if (scaleMax - scaleMin < 2) {
    return [
      { ...EMPTY_SCALE_BUCKET, range_min: scaleMin, range_max: scaleMax, display_heading: 'Thanks for your response!' },
    ];
  }
  const mid = Math.floor((scaleMin + scaleMax) / 2);
  return [
    { ...EMPTY_SCALE_BUCKET, range_min: scaleMin, range_max: mid, engagement_points: 1, display_heading: 'Thanks for your response!' },
    { ...EMPTY_SCALE_BUCKET, range_min: mid + 1, range_max: scaleMax, engagement_points: 5, display_heading: 'Thanks for your response!' },
  ];
}

function makeDefaultOptions() {
  return [
    { ...EMPTY_OPTION, response_value: 'yes', button_label: 'Yes', display_heading: 'Thanks for your response!' },
    { ...EMPTY_OPTION, response_value: 'no', button_label: 'No', display_heading: 'Thanks for your response!' },
  ];
}

async function copyToClipboard(text, setToast) {
  try {
    await navigator.clipboard.writeText(text);
    setToast('Copied to clipboard');
    setTimeout(() => setToast(null), 2000);
  } catch {
    setToast('Copy failed');
    setTimeout(() => setToast(null), 3000);
  }
}

export default function QuestionResponseIntentDialog({ onSubmit, onCancel, onToast, existingIntent, availableTypes, onTypeChange, intentType }) {
  const isEdit = !!existingIntent;
  const [label, setLabel] = useState(existingIntent?.intentCode?.label || '');
  const [questionText, setQuestionText] = useState(existingIntent?.config?.question_text || '');
  const [responseType, setResponseType] = useState(existingIntent?.config?.response_type || 'yes_no');
  const [options, setOptions] = useState(() => {
    if (existingIntent?.options?.length) {
      return existingIntent.options.map((opt) => ({
        response_value: opt.response_value || '',
        button_label: opt.button_label || '',
        engagement_points: opt.engagement_points ?? 0,
        display_heading: opt.display_heading || '',
        display_body: opt.display_body || '',
        display_cta_text: opt.display_cta_text || '',
        display_cta_url: opt.display_cta_url || '',
      }));
    }
    return makeDefaultOptions();
  });
  const [scaleMin, setScaleMin] = useState(() => {
    if (existingIntent?.config?.response_type === 'scale' && existingIntent?.options?.length) {
      return Math.min(...existingIntent.options.map((o) => o.range_min ?? 1));
    }
    return 1;
  });
  const [scaleMax, setScaleMax] = useState(() => {
    if (existingIntent?.config?.response_type === 'scale' && existingIntent?.options?.length) {
      return Math.max(...existingIntent.options.map((o) => o.range_max ?? 10));
    }
    return 10;
  });
  const [scaleBuckets, setScaleBuckets] = useState(() => {
    if (existingIntent?.config?.response_type === 'scale' && existingIntent?.options?.length) {
      return existingIntent.options.map((opt) => ({
        range_min: opt.range_min ?? '',
        range_max: opt.range_max ?? '',
        engagement_points: opt.engagement_points ?? 0,
        display_heading: opt.display_heading || '',
        display_body: opt.display_body || '',
        display_cta_text: opt.display_cta_text || '',
        display_cta_url: opt.display_cta_url || '',
      }));
    }
    return makeDefaultScaleBuckets(1, 10);
  });

  const handleResponseTypeChange = useCallback((newType) => {
    setResponseType(newType);
    if (newType === 'yes_no') {
      setOptions(makeDefaultOptions());
    } else if (newType === 'multiple_choice') {
      setOptions((prev) => prev.length < 2
        ? [...prev, ...makeDefaultOptions().slice(prev.length)]
        : prev
      );
    } else if (newType === 'scale') {
      setScaleBuckets(makeDefaultScaleBuckets(scaleMin, scaleMax));
    }
  }, [scaleMin, scaleMax]);
  const [forwardHeading, setForwardHeading] = useState(
    existingIntent?.config?.forward_capture_heading || "Someone thought you'd find this interesting — that's fantastic!"
  );
  const [forwardBody, setForwardBody] = useState(
    existingIntent?.config?.forward_capture_body || 'Drop your email and name to stay in the loop.'
  );
  const [forwardCtaText, setForwardCtaText] = useState(
    existingIntent?.config?.forward_capture_cta_text || ''
  );
  const [forwardCtaUrl, setForwardCtaUrl] = useState(
    existingIntent?.config?.forward_capture_cta_url || ''
  );
  const [forwardDismissMessage, setForwardDismissMessage] = useState(
    existingIntent?.config?.forward_capture_dismiss_message || "We understand. It’s too soon.\nBrowse the site and see if we are a good fit."
  );
  const [showForwardCapture, setShowForwardCapture] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [createdResult, setCreatedResult] = useState(null);

  const updateOption = useCallback((index, field, value) => {
    setOptions((prev) => prev.map((opt, i) => i === index ? { ...opt, [field]: value } : opt));
  }, []);

  const addOption = useCallback(() => {
    setOptions((prev) => [...prev, { ...EMPTY_OPTION, display_heading: 'Thanks for your response!' }]);
  }, []);

  const removeOption = useCallback((index) => {
    setOptions((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateBucket = useCallback((index, field, value) => {
    setScaleBuckets((prev) => prev.map((b, i) => i === index ? { ...b, [field]: value } : b));
  }, []);

  const addBucket = useCallback(() => {
    setScaleBuckets((prev) => [...prev, { ...EMPTY_SCALE_BUCKET, display_heading: 'Thanks for your response!' }]);
  }, []);

  const removeBucket = useCallback((index) => {
    setScaleBuckets((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const validate = useCallback(() => {
    if (!questionText.trim()) { return 'Question text is required'; }
    if (responseType === 'scale') {
      if (scaleBuckets.length < 2) { return 'At least 2 scoring buckets required'; }
      const sMin = parseInt(scaleMin, 10);
      const sMax = parseInt(scaleMax, 10);
      if (isNaN(sMin) || isNaN(sMax) || sMin >= sMax) { return 'Invalid scale range'; }
      for (let i = 0; i < scaleBuckets.length; i++) {
        const b = scaleBuckets[i];
        const bMin = parseInt(b.range_min, 10);
        const bMax = parseInt(b.range_max, 10);
        if (isNaN(bMin) || isNaN(bMax)) { return `Bucket ${i + 1}: range min and max are required`; }
        if (bMin >= bMax) { return `Bucket ${i + 1}: min must be less than max`; }
        if (!b.display_heading.trim()) { return `Bucket ${i + 1}: display heading is required`; }
      }
      const sorted = [...scaleBuckets].sort((a, b) => parseInt(a.range_min, 10) - parseInt(b.range_min, 10));
      if (parseInt(sorted[0].range_min, 10) !== sMin) { return `Ranges must start at ${sMin}`; }
      if (parseInt(sorted[sorted.length - 1].range_max, 10) !== sMax) { return `Ranges must end at ${sMax}`; }
      for (let i = 1; i < sorted.length; i++) {
        const prevMax = parseInt(sorted[i - 1].range_max, 10);
        const currMin = parseInt(sorted[i].range_min, 10);
        if (currMin !== prevMax + 1) { return `Gap or overlap between buckets at ${prevMax} and ${currMin}`; }
      }
      return null;
    }
    if (options.length < 2) { return 'At least 2 response options required'; }
    for (let i = 0; i < options.length; i++) {
      const opt = options[i];
      if (!opt.response_value.trim()) { return `Option ${i + 1}: response value is required`; }
      if (!opt.button_label.trim()) { return `Option ${i + 1}: button label is required`; }
      if (!opt.display_heading.trim()) { return `Option ${i + 1}: display heading is required`; }
    }
    return null;
  }, [options, responseType, questionText, scaleBuckets, scaleMin, scaleMax]);

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    setSubmitting(true);
    try {
      const scaleOptions = responseType === 'scale'
        ? scaleBuckets.map((b, i) => ({
            response_value: `range_${b.range_min}_${b.range_max}`,
            button_label: `${b.range_min}–${b.range_max}`,
            display_order: i,
            engagement_points: parseInt(b.engagement_points, 10) || 0,
            display_heading: b.display_heading.trim(),
            display_body: b.display_body.trim() || undefined,
            display_cta_text: b.display_cta_text.trim() || undefined,
            display_cta_url: b.display_cta_url.trim() || undefined,
            range_min: parseInt(b.range_min, 10),
            range_max: parseInt(b.range_max, 10),
          }))
        : undefined;

      const payload = {
        label: label || undefined,
        response_type: responseType,
        question_text: questionText.trim() || undefined,
        forward_capture_heading: forwardHeading || undefined,
        forward_capture_body: forwardBody || undefined,
        forward_capture_cta_text: forwardCtaText.trim() || undefined,
        forward_capture_cta_url: forwardCtaUrl.trim() || undefined,
        forward_capture_dismiss_message: forwardDismissMessage.trim() || undefined,
        ...(responseType === 'scale' && { scale_min: parseInt(scaleMin, 10), scale_max: parseInt(scaleMax, 10) }),
        options: scaleOptions || options.map((opt, i) => ({
          response_value: opt.response_value.trim(),
          button_label: opt.button_label.trim(),
          display_order: i,
          engagement_points: parseInt(opt.engagement_points, 10) || 0,
          display_heading: opt.display_heading.trim(),
          display_body: opt.display_body.trim() || undefined,
          display_cta_text: opt.display_cta_text.trim() || undefined,
          display_cta_url: opt.display_cta_url.trim() || undefined,
        })),
      };
      const result = await onSubmit(payload);
      if (isEdit) {
        if (onToast) { onToast('Intent updated'); }
        onCancel();
        return;
      }
      setCreatedResult(result?.data || result);
    } catch {
      // Error toast handled by parent
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px', width: '100%' };
  const labelStyle = { fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' };
  const sectionStyle = { borderTop: '1px solid #e5e7eb', paddingTop: '12px', marginTop: '12px' };

  if (createdResult) {
    const code = createdResult.intentCode?.code || '';
    const baseUrl = `${window.location.origin}/i/${code}`;
    const cidToken = '{{contact.hs_object_id}}';
    const baseUrlWithCid = `${baseUrl}?ref=${cidToken}`;
    const optionUrls = (createdResult.options || []).map((opt) => ({
      label: opt.button_label,
      value: opt.response_value,
      url: `${baseUrl}?v=${opt.response_value}&ref=${cidToken}`,
    }));

    return (
      <div style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}>
        <div style={{
          backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
          maxWidth: '600px', width: '100%', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          maxHeight: '90vh', overflowY: 'auto',
        }}>
          <h3 style={{ margin: '0 0 4px', fontSize: '18px' }}>Intent Created</h3>
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6b7280' }}>
            Code: <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{code}</span>
          </p>

          <div style={{ marginBottom: '16px' }}>
            <div style={labelStyle}>Base URL (Mode B — on-page selection)</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input readOnly value={baseUrlWithCid} style={{ ...inputStyle, backgroundColor: '#f9fafb', fontFamily: 'monospace', fontSize: '11px' }} />
              <button onClick={() => copyToClipboard(baseUrlWithCid, onToast)} style={{
                padding: '6px 12px', borderRadius: '4px', border: '1px solid #d1d5db',
                backgroundColor: '#fff', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap',
              }}>Copy</button>
            </div>
          </div>

          <div style={labelStyle}>Per-Response URLs (Mode A — email link auto-submit)</div>
          {optionUrls.map((opt) => (
            <div key={opt.value} style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '12px', color: '#374151', marginBottom: '2px' }}>{opt.label} ({opt.value})</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input readOnly value={opt.url} style={{ ...inputStyle, backgroundColor: '#f9fafb', fontFamily: 'monospace', fontSize: '11px' }} />
                <button onClick={() => copyToClipboard(opt.url, onToast)} style={{
                  padding: '6px 12px', borderRadius: '4px', border: '1px solid #d1d5db',
                  backgroundColor: '#fff', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap',
                }}>Copy</button>
              </div>
            </div>
          ))}

          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px', marginBottom: '16px' }}>
            Replace {'{{contact.hs_object_id}}'} with the recipient{"'"}s contact ID. Paste these URLs directly into email buttons.
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={onCancel} style={{
              padding: '8px 16px', borderRadius: '6px', border: 'none',
              backgroundColor: '#3b82f6', color: '#fff', cursor: 'pointer',
              fontSize: '14px', fontWeight: 600,
            }}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
        maxWidth: '640px', width: '100%', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>{isEdit ? 'Edit Question Response Intent' : 'New Question Response Intent'}</h3>

        {availableTypes && availableTypes.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Intent Type *</label>
            <select style={inputStyle} value={intentType} onChange={(e) => onTypeChange(e.target.value)}>
              {availableTypes.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        )}

        {validationError && (
          <div role="alert" style={{
            padding: '8px 12px', backgroundColor: '#fee2e2', color: '#dc2626',
            borderRadius: '6px', fontSize: '13px', marginBottom: '12px',
          }}>{validationError}</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Label</label>
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Pain Point Survey — Email 2" style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Question Text *<InfoBubble text="The question shown to the visitor on the response page. This is what they see above the answer buttons." /></label>
            <textarea value={questionText} onChange={(e) => setQuestionText(e.target.value)}
              placeholder="e.g., Are you interested in guided outdoor experiences?" rows={3}
              style={{ ...inputStyle, resize: 'vertical' }} />
          </div>

          <div>
            <label style={labelStyle}>Response Type</label>
            <select value={responseType} onChange={(e) => handleResponseTypeChange(e.target.value)} style={inputStyle}>
              {RESPONSE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div style={sectionStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ ...labelStyle, marginBottom: 0 }}>{responseType === 'scale' ? 'Scoring Buckets *' : 'Response Options *'}</span>
              {responseType === 'multiple_choice' && (
                <button onClick={addOption} type="button" style={{
                  padding: '4px 10px', borderRadius: '4px', border: '1px solid #d1d5db',
                  backgroundColor: '#fff', cursor: 'pointer', fontSize: '12px',
                }}>+ Add option</button>
              )}
              {responseType === 'scale' && (
                <button onClick={addBucket} type="button" style={{
                  padding: '4px 10px', borderRadius: '4px', border: '1px solid #d1d5db',
                  backgroundColor: '#fff', cursor: 'pointer', fontSize: '12px',
                }}>+ Add bucket</button>
              )}
            </div>

            {responseType === 'scale' && (
              <>
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>Scale Range<InfoBubble text="The min and max values visitors can select. Buckets must cover this entire range." /></label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                    {SCALE_PRESETS.map((p) => (
                      <button key={p.label} type="button" onClick={() => { setScaleMin(p.min); setScaleMax(p.max); setScaleBuckets(makeDefaultScaleBuckets(p.min, p.max)); }}
                        style={{
                          padding: '4px 10px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer',
                          border: `1px solid ${parseInt(scaleMin, 10) === p.min && parseInt(scaleMax, 10) === p.max ? '#3b82f6' : '#d1d5db'}`,
                          backgroundColor: parseInt(scaleMin, 10) === p.min && parseInt(scaleMax, 10) === p.max ? '#eff6ff' : '#fff',
                          color: parseInt(scaleMin, 10) === p.min && parseInt(scaleMax, 10) === p.max ? '#3b82f6' : '#374151',
                        }}>{p.label}</button>
                    ))}
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>or custom:</span>
                    <input type="number" value={scaleMin} onChange={(e) => setScaleMin(e.target.value)}
                      style={{ ...inputStyle, width: '60px' }} aria-label="Scale minimum" />
                    <span style={{ color: '#6b7280' }}>–</span>
                    <input type="number" value={scaleMax} onChange={(e) => setScaleMax(e.target.value)}
                      style={{ ...inputStyle, width: '60px' }} aria-label="Scale maximum" />
                  </div>
                </div>

                {scaleBuckets.map((bucket, idx) => (
                  <div key={idx} style={{
                    border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px',
                    marginBottom: '8px', backgroundColor: '#fafafa',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>Bucket {idx + 1}</span>
                      {scaleBuckets.length > 2 && (
                        <button onClick={() => removeBucket(idx)} type="button" style={{
                          padding: '2px 6px', borderRadius: '4px', border: '1px solid #fca5a5',
                          backgroundColor: '#fff', cursor: 'pointer', fontSize: '11px', color: '#ef4444',
                        }}>Remove</button>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                      <div>
                        <label style={labelStyle}>Range Min *</label>
                        <input type="number" value={bucket.range_min} onChange={(e) => updateBucket(idx, 'range_min', e.target.value)}
                          style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>Range Max *</label>
                        <input type="number" value={bucket.range_max} onChange={(e) => updateBucket(idx, 'range_max', e.target.value)}
                          style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>Engagement Points</label>
                        <input type="number" value={bucket.engagement_points} onChange={(e) => updateBucket(idx, 'engagement_points', e.target.value)}
                          style={inputStyle} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={labelStyle}>Display Heading *</label>
                        <input type="text" value={bucket.display_heading} onChange={(e) => updateBucket(idx, 'display_heading', e.target.value)}
                          placeholder="Thanks for your response!" style={inputStyle} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={labelStyle}>Display Body</label>
                        <input type="text" value={bucket.display_body} onChange={(e) => updateBucket(idx, 'display_body', e.target.value)}
                          placeholder="Optional follow-up text" style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>CTA Text</label>
                        <input type="text" value={bucket.display_cta_text} onChange={(e) => updateBucket(idx, 'display_cta_text', e.target.value)}
                          placeholder="Learn more" style={inputStyle} />
                      </div>
                      <div style={{ gridColumn: '2 / -1' }}>
                        <label style={labelStyle}>CTA URL</label>
                        <input type="text" value={bucket.display_cta_url} onChange={(e) => updateBucket(idx, 'display_cta_url', e.target.value)}
                          placeholder="https://..." style={inputStyle} />
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {responseType !== 'scale' && options.map((opt, idx) => (
              <div key={idx} style={{
                border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px',
                marginBottom: '8px', backgroundColor: '#fafafa',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>Option {idx + 1}</span>
                  {responseType === 'multiple_choice' && options.length > 2 && (
                    <button onClick={() => removeOption(idx)} type="button" style={{
                      padding: '2px 6px', borderRadius: '4px', border: '1px solid #fca5a5',
                      backgroundColor: '#fff', cursor: 'pointer', fontSize: '11px', color: '#ef4444',
                    }}>Remove</button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <label style={labelStyle}>Response Value *</label>
                    <input type="text" value={opt.response_value} onChange={(e) => updateOption(idx, 'response_value', e.target.value)}
                      placeholder="yes" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Button Label *</label>
                    <input type="text" value={opt.button_label} onChange={(e) => updateOption(idx, 'button_label', e.target.value)}
                      placeholder="YES, absolutely" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Engagement Points</label>
                    <input type="number" value={opt.engagement_points} onChange={(e) => updateOption(idx, 'engagement_points', e.target.value)}
                      style={inputStyle} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Display Heading *<InfoBubble text="Headline shown to the user on the confirmation page after they respond." /></label>
                    <input type="text" value={opt.display_heading} onChange={(e) => updateOption(idx, 'display_heading', e.target.value)}
                      placeholder="Thanks for your response!" style={inputStyle} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Display Body<InfoBubble text="Optional subtext shown below the heading on the confirmation page." /></label>
                    <input type="text" value={opt.display_body} onChange={(e) => updateOption(idx, 'display_body', e.target.value)}
                      placeholder="Optional follow-up text" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>CTA Button Text<InfoBubble text="Label for an optional button on the confirmation page. Leave blank for no button." /></label>
                    <input type="text" value={opt.display_cta_text} onChange={(e) => updateOption(idx, 'display_cta_text', e.target.value)}
                      placeholder="Learn more" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>CTA URL<InfoBubble text="Where the CTA button links to (e.g., a booking page or guide profile). Leave blank for no button." /></label>
                    <input type="text" value={opt.display_cta_url} onChange={(e) => updateOption(idx, 'display_cta_url', e.target.value)}
                      placeholder="https://..." style={inputStyle} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <details open={showForwardCapture} onToggle={(e) => setShowForwardCapture(e.target.open)}>
            <summary style={{ fontSize: '13px', fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
              Forward Capture Settings
            </summary>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
              <div>
                <label style={labelStyle}>Heading</label>
                <input type="text" value={forwardHeading} onChange={(e) => setForwardHeading(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Body</label>
                <textarea value={forwardBody} onChange={(e) => setForwardBody(e.target.value)}
                  rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
              <div>
                <label style={labelStyle}>Dismiss Message<InfoBubble text="Shown when visitor clicks 'No thanks'. Use line breaks for formatting." /></label>
                <textarea value={forwardDismissMessage} onChange={(e) => setForwardDismissMessage(e.target.value)}
                  rows={2} style={{ ...inputStyle, resize: 'vertical' }}
                  placeholder="We understand. It's too soon.&#10;Browse the site and see if we are a good fit." />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={labelStyle}>Dismiss CTA Text<InfoBubble text="Button shown when visitor clicks 'No thanks'. Leave blank for no button." /></label>
                  <input type="text" value={forwardCtaText} onChange={(e) => setForwardCtaText(e.target.value)}
                    placeholder="Learn more" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Dismiss CTA URL<InfoBubble text="Where the dismiss CTA links to (e.g., coming-soon page or homepage)." /></label>
                  <input type="text" value={forwardCtaUrl} onChange={(e) => setForwardCtaUrl(e.target.value)}
                    placeholder="https://..." style={inputStyle} />
                </div>
              </div>
            </div>
          </details>
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button onClick={onCancel} style={{
            padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db',
            backgroundColor: '#fff', cursor: 'pointer', fontSize: '14px',
          }}>Cancel</button>
          <button
            disabled={submitting}
            onClick={handleSubmit}
            style={{
              padding: '8px 16px', borderRadius: '6px', border: 'none',
              backgroundColor: '#f59e0b', color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: 600, opacity: submitting ? 0.6 : 1,
            }}
          >{submitting ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Intent')}</button>
        </div>
      </div>
    </div>
  );
}
